import React from "react";
import {EditBudgetModal} from "../../../client/EditBudgetModal";
import {TYPE_ADGROUP, TYPE_BUSINESS_UNIT, TYPE_CAMPAIGN, TYPE_CAMPAIGNS_CONFIGURATION} from "../DashboardRow";
import {ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS, ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS, ALLOCATED_AMOUNT_TYPE_TOTAL} from "../../../business_units/BudgetTypes";
import {
    INFORMATIVE_LOCALE_CLASSIC, INFORMATIVE_MATCH, INFORMATIVE_MISSING, compareInformativeBudget, formatInformativeMoney,
} from "../../../elements/informativeBudget";
import i18n from "../../../../i18n";
import {BudgetCellTooltip} from "./base/BudgetCellTooltip";
import {FaIcon} from "../../../FaIcon";
import {DashboardEditableCell} from "./base/DashboardEditableCell";

export class DashboardBudgetCell extends DashboardEditableCell {
    propForOnItemUpdated() {
        return this.props.onBudgetUpdated;
    }

    renderTooltip() {
        if(!this.hasPartialGoals())
            return null;

        return <BudgetCellTooltip
            periodType={this.props.periodType}
            partialGoals={this.props.budgetPartialGoals}
            inputDefinition={this.props.budgetPartialGoalsInputDefinition}
            totalBudget={this.props.value}
            formatedTotalBudget={this.props.formattedValue}
            currency={this.props.businessUnitCurrency}
        />;
    }

    shouldRenderCellContent() {
        // Fila de un Bucc
        if(this.props.rowType === TYPE_CAMPAIGNS_CONFIGURATION)
            return false;

        // Fila de una campaña: con presupuesto a nivel campaña, o INFORMATIVO en una línea global
        // (card CvdQbnl2: se carga por campaña como los objetivos, sin cambiar el global).
        if(this.props.rowType === TYPE_CAMPAIGN && this.props.budgetType !== ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS
            && !this.isInformativeCampaign())
            return false;

        // Fila de un grupo de anuncios y presupuesto NO a nivel grupo de anuncios
        if(this.props.rowType === TYPE_ADGROUP && this.props.budgetType !== ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS)
            return false;

        return true;
    }

    isInformativeCampaign() {
        return this.props.rowType === TYPE_CAMPAIGN && this.props.budgetType === ALLOCATED_AMOUNT_TYPE_TOTAL;
    }

    // Un 0 es «sin monto»: cargar 0 es la forma de quitarlo, y el backend borra la fila.
    hasInformativeValue() {
        return Number(this.state.value) > 0;
    }

    /**
     * En la fila de una línea global: ¿los presupuestos informativos de sus campañas coinciden con el
     * global? Sólo se mira cuando hay alguno cargado (`informativeCampaignsBudgetSum` no es `null`).
     */
    informativeComparison() {
        if(this.props.rowType !== TYPE_BUSINESS_UNIT)
            return null;
        return compareInformativeBudget(this.props.value, this.props.informativeCampaignsBudgetSum);
    }

    /**
     * El ícono ámbar de la fila de la línea cuando sus campañas no suman lo mismo que el global: el
     * desfasaje se ve escaneando el Tablero, sin abrir nada. Nunca bloquea (acuerdo con Pablo).
     */
    renderInformativeMismatch() {
        const comparison = this.informativeComparison();
        if(comparison === null || comparison.state === INFORMATIVE_MATCH)
            return null;
        const t = i18n.t.bind(i18n);
        const currency = this.props.businessUnitCurrency;
        // Con el formato de las celdas del Tablero («$ 50,000»), no el del Panel 2.1.
        const money = amount => formatInformativeMoney(currency, amount, INFORMATIVE_LOCALE_CLASSIC);
        const params = {sum: money(this.props.informativeCampaignsBudgetSum),
                        line: money(this.props.value),
                        difference: money(comparison.difference),
                        interpolation: {escapeValue: false}};
        const text = comparison.state === INFORMATIVE_MISSING ? t('informative_budget.missing', params) : t('informative_budget.excess', params);
        return <div key="informative-mismatch" className="warning-icon-container informative-budget-mismatch me-2">
            <FaIcon iconName="exclamation-triangle"/>
            <div className="warning-tooltip -below-right">
                <div className="warning-tooltip-body">
                    <p className="warning-tooltip-text">{text}</p>
                </div>
            </div>
        </div>;
    }

    hasPartialGoals() {
        return this.props.budgetPartialGoals && this.props.budgetPartialGoals.length > 0;
    }

    editionDisabled() {
        return this.props.rowType === TYPE_BUSINESS_UNIT && (this.props.budgetType === ALLOCATED_AMOUNT_TYPE_SUM_OF_CAMPAIGNS || this.props.budgetType === ALLOCATED_AMOUNT_TYPE_SUM_OF_ADGROUPS);
    }

    renderCellContent() {
        const itemsToRender = [];
        itemsToRender.push(this.renderWarningIfNecessary());
        itemsToRender.push(this.renderInformativeMismatch());
        if(this.isInformativeCampaign()) {
            // Sin monto cargado la celda queda VACÍA: sólo el lápiz, que aparece al pasar el mouse. Así
            // una línea global sin presupuestos por campaña se ve igual que antes de esta card.
            if(this.hasInformativeValue())
                itemsToRender.push(<span key="informative" className="informative-budget-value">{this.state.formattedValue}</span>);
        }
        else if(!this.hasPartialGoals())
            itemsToRender.push(this.state.formattedValue);
        else
            itemsToRender.push(<span className={"has-toolbox"}>{this.state.formattedValue}&nbsp;&nbsp;&nbsp;<FaIcon iconName="list-ol"/></span>);
        return itemsToRender;
    }

    renderEditModal() {
        return <EditBudgetModal onFormSubmitted={this.handleItemModified}
                                businessUnitId={this.props.businessUnitId}
                                campaignsConfigurationId={this.props.campaignsConfigurationId}
                                campaignId={this.props.campaignId}
                                adgroupId={this.props.adgroupId}
                                value={this.props.value}
                                periodType={this.props.periodType}
                                fromDate={this.props.fromDate}
                                toDate={this.props.toDate}
                                budgetPartialGoals={this.props.budgetPartialGoals}
                                budgetPartialGoalsInputDefinition={this.props.budgetPartialGoalsInputDefinition}
                                clickOutsideDontCloseModal={true}
                                clientName={this.props.clientName}
                                businessUnitName={this.props.businessUnitName}/>;
    }
}
